import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TermsModal({ isOpen, onClose }: TermsModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-4xl bg-white rounded-lg shadow-2xl max-h-[90vh] overflow-hidden"
            >
              {/* Header with close button */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">
                  Terms of Use & Disclaimer
                </h1>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="prose prose-gray max-w-none">
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                    <p className="text-blue-800 text-sm">
                      Welcome to the S3 Portal application ("Application"). This Application has been developed and is owned by Operisoft Technologies Private Limited ("Operisoft").
                    </p>
                    <p className="text-blue-700 mt-2 text-sm font-medium">
                      By accessing or using this Application, you agree to the following terms of use and disclaimers.
                    </p>
                  </div>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Ownership</h2>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                      <li>The Application is the sole intellectual property of Operisoft Technologies Private Limited.</li>
                      <li>Deployment of this Application into a customer's AWS account is part of Operisoft's managed services offering.</li>
                      <li>No software license is sold, transferred, or separately granted. Access is strictly limited to customers who receive Operisoft's managed services.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Deployment & Access</h2>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                      <li>The Application is deployed within the customer's AWS account by Operisoft.</li>
                      <li>The customer is responsible for granting and managing user access to the Application.</li>
                      <li>Operisoft may update, modify, or withdraw the Application at its discretion as part of service improvements.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Data & Security Responsibility</h2>
                    <p className="text-gray-700 mb-3">All data accessed or managed through this Application remains within the customer's AWS account.</p>
                    <p className="text-gray-700 mb-3">The customer bears full responsibility for:</p>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                      <li>Security, compliance, and access control of their AWS resources.</li>
                      <li>Ensuring appropriate data governance, encryption, and backup practices.</li>
                    </ul>
                    <p className="text-gray-700">Operisoft does not monitor, store, or control customer data and shall not be liable for any data loss, corruption, or unauthorized access.</p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Disclaimer of Warranties</h2>
                    <p className="text-gray-700 mb-3">The Application is provided "as is" without warranties of any kind, either express or implied.</p>
                    <p className="text-gray-700 mb-3">Operisoft does not guarantee:</p>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                      <li>Error-free or uninterrupted operation.</li>
                      <li>Compatibility with all AWS services or configurations.</li>
                      <li>Suitability for any specific business purpose.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Limitation of Liability</h2>
                    <p className="text-gray-700 mb-3">Operisoft shall not be liable for any damages arising from the use or inability to use this Application, including but not limited to:</p>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                      <li>Data loss or corruption.</li>
                      <li>Unauthorized access or breaches.</li>
                      <li>Business interruption or financial loss.</li>
                    </ul>
                    <p className="text-gray-700">Customers are advised to implement adequate controls and monitoring of their AWS environment.</p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Governing Law</h2>
                    <p className="text-gray-700 mb-4">
                      These Terms shall be governed by and construed in accordance with the laws of India, without regard to conflict of law provisions.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Contact Information</h2>
                    <p className="text-gray-700 mb-3">For any questions, support, or clarifications regarding this Application, please contact:</p>
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <p className="text-gray-700">
                        <strong>Operisoft Technologies Private Limited</strong><br />
                        Office No. 301, Zion Complex Building, Plot No 273,<br />
                        Sector – 10, Kharghar, Navi Mumbai – 410210, MH, India<br />
                        Email: support@operisoft.com<br />
                        Phone: 07941056868
                      </p>
                    </div>
                  </section>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}